import { useActionConfirmation } from '../../components/ActionFeedbackProvider';
// React and hooks
import React, { useState, useEffect } from 'react';
// Icons
import { Trash, Plus, ChevronLeft, ChevronRight, Search, Pencil, FileDown } from 'lucide-react';
import { FaWarehouse } from 'react-icons/fa';
// Router
import { useNavigate } from 'react-router-dom';
// Animations
import { motion } from 'framer-motion';
// Hooks
import useTheme from '../../hooks/useTheme';
// API client
import api from '../../lib/api';
// Modal components
import AddEntryModal from '../../components/modals/AddEntryModal';
import EditEntryModal from '../../components/modals/EditEntryModal';
// i18n
import { useTranslation } from 'react-i18next';

const EntrySTotems = () => {
  // States
  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);

  // NEW: edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');

  // Hooks
  const navigate = useNavigate();
  const { t } = useTranslation();
  const confirmAction = useActionConfirmation();

  const { theme } = useTheme();

  const locationKey = 'totems';

  const baseBtn =
    'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition duration-200 ' +
    'bg-black text-white hover:bg-white hover:text-black hover:scale-105 ' +
    'dark:bg-white dark:text-black dark:hover:bg-black dark:hover:text-white';

  const actionBtn =
    'inline-flex items-center justify-center p-2.5 rounded-md shadow transition duration-200 text-sm ' +
    'bg-black text-white hover:bg-white hover:text-black hover:scale-105 ' +
    'dark:bg-white dark:text-black dark:hover:bg-black dark:hover:text-white';

  const getId = (o) => o?.id || o?._id;

  // FIX: Local date format
  const formatDate = (date) => date.toISOString().split('T')[0];

  const getDayName = (date) =>
    date.toLocaleDateString(undefined, { weekday: 'long' });

  const changeDate = (direction) => {
    const nd = new Date(selectedDate);
    nd.setDate(nd.getDate() + direction);
    setSelectedDate(nd);
  };

  const filterEntries = (list, date, term) => {
    const filtered = list.filter((entry) => {
      const d = new Date(entry.enteredAt);

      const sameDay =
        d.getFullYear() === date.getFullYear() &&
        d.getMonth() === date.getMonth() &&
        d.getDate() === date.getDate();

      const searchMatch =
        (entry.productName || '').toLowerCase().includes(term.toLowerCase()) ||
        (entry.brandName || '').toLowerCase().includes(term.toLowerCase()) ||
        (entry.supplierName || '').toLowerCase().includes(term.toLowerCase()) ||
        (entry.notes || '').toLowerCase().includes(term.toLowerCase());

      return sameDay && searchMatch;
    });

    setFilteredEntries(filtered);
  };

  const loadEntries = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/inventory/entries?location=${locationKey}`);
      const arr = Array.isArray(data) ? data.slice().reverse() : [];
      setEntries(arr);
      filterEntries(arr, selectedDate, searchTerm);
    } catch (err) {
      setError(err?.response?.data?.message || t('errorLoadEntries'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEntries();
  }, []);

  useEffect(() => {
    filterEntries(entries, selectedDate, searchTerm);
  }, [entries, selectedDate, searchTerm]);

  const handleDelete = async (id) => {
    if (!await confirmAction(t('confirmDeleteEntry'))) return;
    try {
      await api.delete(`/inventory/entries/${id}`);
      await loadEntries();
    } catch (err) {
      setError(err?.response?.data?.message || t('errorDeleteEntry'));
    }
  };

  const handleExport = async (id) => {
    try {
      const res = await api.get(`/inventory/entries/${id}/export`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `entry_${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      alert(t('errorExport'));
    }
  };

  // NEW: open edit modal
  const handleEdit = (entry) => {
    setSelectedRecord(entry);
    setShowEditModal(true);
  };

  return (
    <motion.div
      className="px-1 py-4 sm:px-3 sm:py-6 lg:px-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      {/* Date selector */}
      <div className="flex flex-col items-center mb-6 select-none">
        <div className="flex items-center gap-2">
          <button
            onClick={() => changeDate(-1)}
            className="p-2 rounded-md bg-black text-white dark:bg-white dark:text-black hover:bg-white hover:text-black dark:hover:bg-black dark:hover:text-white transition"
          >
            <ChevronLeft size={18} />
          </button>

          <input
            type="date"
            value={formatDate(selectedDate)}
            onChange={(e) => setSelectedDate(new Date(e.target.value))}
            className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 text-sm dark:bg-gray-800 dark:text-white"
          />

          <button
            onClick={() => changeDate(1)}
            className="p-2 rounded-md bg-black text-white dark:bg-white dark:text-black hover:bg-white hover:text-black dark:hover:bg-black dark:hover:text-white transition"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        <h2 className="mt-2 text-xl font-bold text-gray-900 dark:text-white capitalize">
          {getDayName(selectedDate)}
        </h2>
      </div>

      <h1 className="text-2xl font-bold text-center mb-4 dark:text-white">
        {t('entriesTotems')}
      </h1>

      {/* Search */}
      <div className="max-w-3xl mx-auto mb-6 relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder={t('searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        />
      </div>

      <div className="max-w-5xl mx-auto bg-white dark:bg-gray-800 shadow-xl rounded-xl p-3 sm:p-4 lg:p-6">
        {/* Actions */}
        <div className="flex flex-col gap-3 mb-5 sm:flex-row sm:items-center sm:justify-between">
          <div />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setShowModal(true)} className={baseBtn}>
              <Plus size={16} /> {t('addEntry')}
            </button>
            <button
              onClick={() => navigate('/products/inventory/totems')}
              className={baseBtn}
            >
              <FaWarehouse /> {t('inventory')}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-600/90 text-white px-4 py-2 rounded-lg mb-3">{error}</div>
        )}

        {loading ? (
          <div className="text-gray-700 dark:text-gray-200">{t('loading')}</div>
        ) : filteredEntries.length ? (
          <div className="space-y-2">
            {filteredEntries.map((entry) => (
              <div
                key={getId(entry)}
                className="border border-gray-300 dark:border-gray-700 p-3 rounded-md flex justify-between items-center dark:bg-gray-700"
              >
                {/* info */}
                <div className="flex flex-col text-sm dark:text-gray-200 w-full leading-tight">

                  <p className="font-semibold text-base dark:text-white truncate">
                    {entry.productName}
                  </p>

                  <p><span className="font-medium">{t('brand')}:</span> {entry.brandName}</p>
                  <p><span className="font-medium">{t('supplier')}:</span> {entry.supplierName}</p>

                  <p>
                    <span className="font-medium">{t('total')}:</span> {(entry.totalWeight ?? 0).toFixed(2)} KG •{' '}
                    <span className="font-medium">{t('qty')}:</span> {entry.quantityEntered}
                  </p>

                  {entry.notes && (
                    <p className="italic"><span className="font-medium">{t('notes')}:</span> {entry.notes}</p>
                  )}

                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(entry.enteredAt).toLocaleString()}
                  </p>
                  {entry.userId && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      <span className="font-medium">{t('createdBy')}:</span> {entry.userId}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 ml-4 shrink-0">
                  <button onClick={() => handleEdit(entry)} className={actionBtn}>
                    <Pencil size={17} />
                  </button>
                  <button onClick={() => handleExport(getId(entry))} className={actionBtn}>
                    <FileDown size={17} />
                  </button>
                  <button onClick={() => handleDelete(getId(entry))} className={actionBtn}>
                    <Trash size={17} />
                  </button>
                </div>

              </div>
            ))}
          </div>
        ) : (
          <div className="border border-dashed dark:border-gray-700 p-5 rounded-md text-gray-500 dark:text-gray-300 text-center">
            {t('noEntriesForThisDate')}
          </div>
        )}
      </div>

      {/* Add modal */}
      {showModal && (
        <AddEntryModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onCreated={loadEntries}
          locationKey={locationKey}
        />
      )}

      {/* EDIT MODAL */}
      {showEditModal && (
        <EditEntryModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          record={selectedRecord}
          locationKey={locationKey}
          onUpdated={loadEntries}
        />
      )}
    </motion.div>
  );
};

export default EntrySTotems;
